import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { PersonPlus, X, Send } from 'react-bootstrap-icons';
import { useAuth0 } from '@auth0/auth0-react';
import utils from '../../utils';
import ToastDynamic from '../Layout/ToastDynamic/ToastDynamic';
import LBSelect from '../DesignSystem/LBSelect/LBSelect';
import WorkspaceAppAccessSection from './WorkspaceAppAccessSection';
import LBInput from '../DesignSystem/LBInput/LBInput';

function AddNewUser({ tenID, wrkspcList=[] }) {
    const [show, setShow] = useState(false);
    const [workspaceAccess, setWorkspaceAccess] = useState({
        workspaceSelections: {},
        workspacesForSubmit: []
    });
    const [workspaceAccessResetSignal, setWorkspaceAccessResetSignal] = useState(0);
    const [formErrors, setFormErrors] = useState({});
    const workspaceData = wrkspcList;
    const [submitStatus, setSubmitStatus] = useState(null);
    const [usr, setUsr] = useState({
        firstName: null,
        lastName: null,
        emailAddress: null,
        securityLevel: null,
        defaultWorkspace: null
    });
    
    const { getAccessTokenSilently, user } = useAuth0()

     // secLevel options
    const secLevelOptions = Array.from({ length: 6 }, (_, i) => i + 1);   

    const checkForErrors = () => {
        let errors = {};
        if(!usr.firstName) {
            errors.firstNameError = '* First name is required';
        }
        if(!usr.lastName) {
            errors.lastNameError = '* Last name is required';
        }
        if(!usr.emailAddress) {
            errors.emailError = '* Email address is required';
        }
        if(!usr.securityLevel) {
            errors.securityLevelError = '* Security level is required';
        }
        if(!usr.defaultWorkspace) {
            errors.workspaceError = '* Default workspace selection is required';
        }
        if((workspaceAccess.workspacesForSubmit || []).length === 0) {
            errors.workspaceSelectionError = '* User must be assigned to at least one workspace';
        }
        return errors;
    }

    const handleClose = () => { 
        setShow(false);
        setFormErrors({});
        setUsr({
            firstName: null,
            lastName: null,
            emailAddress: null,
            securityLevel: null,
            defaultWorkspace: null
        });
        setWorkspaceAccess({ workspaceSelections: {}, workspacesForSubmit: [] });
        setWorkspaceAccessResetSignal((prev) => prev + 1);
        setSubmitStatus(null);
    };

    const submitNewUser = async () => {
        let theAccToken = await utils.getAccessToken(getAccessTokenSilently, user);
        const tenID = usr.defaultWorkspace; // Get tenant ID from user
        const errors = checkForErrors();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setSubmitStatus('error');
            return;
        }

        try {
            // create the user in auth0
            let res = await fetch(`${process.env.REACT_APP_WEB_API_B2C}/api/users/invite-user?tenid=${tenID}`,{
                method:'POST',
                headers: {
                    authorization: 'Bearer ' + theAccToken.accessToken
                },
                body: JSON.stringify({
                    email: usr.emailAddress,
                    given_name: usr.firstName,
                    family_name: usr.lastName,
                    securityLevel: usr.securityLevel,
                    defaultWorkspace: usr.defaultWorkspace,
                    workspaces: workspaceAccess.workspacesForSubmit
                })
            });

            if(res.ok) {
                setFormErrors({});
            } else {
                setFormErrors({...formErrors, sendError: '* Something went wrong.  Please try again.'});
                setSubmitStatus('error');
                return;
            }
        } catch (error) {
            console.error('Error inviting user:', error);
            setFormErrors({...formErrors, sendError: '* Something went wrong.  Please try again.'});
            setSubmitStatus('error');
            return;
        }

        
        setFormErrors({});
        setUsr({
            firstName: null,
            lastName: null,
            emailAddress: null,
            securityLevel: null,
            defaultWorkspace: null
        });
        setWorkspaceAccess({ workspaceSelections: {}, workspacesForSubmit: [] });
        setWorkspaceAccessResetSignal((prev) => prev + 1);
        setSubmitStatus('success');
    }

    return (
        <>        
            <Button onClick={() => setShow(true)} className="label-medium display-flex justify-center link-primary border-none surface-transparent">Not finding a user? Try adding them first</Button>

            <Modal className="invite-modal m-b-start-07" show={show} onHide={handleClose}>
                <Modal.Header className='display-flex align-items-center justify-between '>
                    <div className='display-flex align-items-center'>
                        <PersonPlus className='m-i-end-03' size={20}/>
                        <Modal.Title className='heading-h3'>Add a new user to Workspace</Modal.Title>
                    </div>
                    
                    <X className='cursor-pointer' size={20} onClick={handleClose} />
                </Modal.Header>
                <Modal.Body>
                    <p className='label-medium'>Add a completely new user to Workspace. This will send a welcome email to the user.</p>
                    <Form>
                        <Row className='m-b-end-06 g-3'>
                            <Col xs={6}>
                                <LBInput
                                    label="First Name"
                                    placeholder="Enter first name"
                                    value={usr.firstName || ''}
                                    onChange={(e) => setUsr({
                                        ...usr,
                                        firstName: e.target.value
                                    })}
                                />
                            </Col>
                            <Col xs={6}>
                                <LBInput
                                    label="Last Name"
                                    placeholder="Enter last name"
                                    value={usr.lastName || ''}
                                    onChange={(e) => setUsr({
                                        ...usr,
                                        lastName: e.target.value
                                    })}
                                />
                            </Col>
                        </Row>

                        <LBInput
                            label="Email"
                            placeholder="Enter their email"
                            type="email"
                            className='m-b-end-06'
                            value={usr.emailAddress || ''}
                            onChange={(e) => setUsr({
                                ...usr,
                                emailAddress: e.target.value
                            })}
                        />

                        <LBSelect 
                            label="Security Level"
                            className='p-b-end-06'
                            fieldName={'secLevelSelect'}
                            value={usr.securityLevel}
                            onChange={(e) => setUsr({
                                ...usr,
                                securityLevel: e
                            })}
                            options={secLevelOptions.map(level => ({ label: `Level ${level}`, value: level }))}
                        />

                        <LBSelect 
                            label="Default Workspace"
                            className='p-b-end-06'
                            fieldName={'defaultWorkspaceSelect'}
                            value={usr.defaultWorkspace}
                            onChange={(e) => setUsr({
                                ...usr,
                                defaultWorkspace: e
                            })}
                            options={wrkspcList.map(ws => ({ label: ws.tenantName, value: ws.tenantID }))}
                            placeholder="Select a workspace"
                        />

                        <WorkspaceAppAccessSection
                            workspaceData={workspaceData}
                            onAccessChange={setWorkspaceAccess}
                            resetSignal={workspaceAccessResetSignal}
                        />

                        {/* Display form errors */}
                        {Object.values(formErrors).length > 0 && (
                            <div className='error-container m-t-end-05 text-danger align-right'>
                                {Object.values(formErrors).map((error, index) => (
                                    <p key={index} className='body-small-condensed error-text'>{error}</p>
                                ))}
                            </div>
                        )}

                        <Modal.Footer className='justify-end p-t-end-05 border-none'>
                            <Button className='btn-sm-invisible' onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button className='btn-sm-solid button-primary' onClick={submitNewUser}>
                                <Send className='m-i-end-02' size={16}/>
                                Add User
                            </Button>
                        </Modal.Footer>
                    </Form>
                    {submitStatus === 'success' ?
                        <ToastDynamic show={true} variant="success" content={`Successfully invited user to workspace.`} handleCloseToast={() => setSubmitStatus(null)} />
                    : submitStatus === 'error' && <ToastDynamic show={true} variant="error" content={'Error submitting invite'} handleCloseToast={() => setSubmitStatus(null)} />}
                </Modal.Body>
            </Modal>
        </>
    );
}

export default AddNewUser;
